import React from 'react';
import { Link, type RouteComponentProps } from '@gatsbyjs/reach-router';
import { useRecoilState } from 'recoil';
import { biedwijzerContentState } from '../..';
import AppDetailsContainer from '../../../components/AppDetailsContainer';

import { StepName } from '../MainPreview/types';
import MainPreview from '../MainPreview';
import ToggleCheckbox from '~/components/molecule/ToggleCheckbox';
import type { AppStatus_Biedwijzer_Report_Cta } from '~/graphql/types';
import JustificationContainer from '~/components/atom/JustificationContainer';
import styled from 'styled-components';
import { Body } from '~/components/atom/Typography';
import Divider from '~/components/atom/Divider';

const text = {
  header: 'Rapport',
  helpWithBiddingLabel: `Toon button 'Plan een bezichtiging in'`,
  helpWithViewingLabel: `Toon button 'Help mij met het uitbrengen van een bod'`,
};

type Props = RouteComponentProps;

const Report: React.FCC<Props> = () => {
  const [values, setValues] = useRecoilState(biedwijzerContentState);

  const onUpdateCta = (
    fieldName: keyof AppStatus_Biedwijzer_Report_Cta,
    value: boolean,
  ) => {
    setValues(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        content: prev.content
          ? {
              ...prev.content,
              report: {
                ...prev.content.report,
                cta: {
                  ...prev.content.report.cta,
                  [fieldName]: value,
                },
              },
            }
          : prev.content,
      };
    });
  };

  if (!values) return null;

  return (
    <AppDetailsContainer header={text.header} icon="file-text">
      <MainPreview
        values={values?.content}
        data={{
          appConfigData: {
            report: {
              cta: {
                helpWithBidding:
                  values?.content?.report.cta.helpWithBidding || false,
                helpWithViewing:
                  values?.content?.report.cta.helpWithViewing || false,
              },
            },
          },
        }}
        step={StepName.Report}
      />
      <Container direction="column" width="100%" padding={['base', null]}>
        <Body>
          Deze buttons worden aan de gebruiker getoond als de woning die wordt
          bekeken binnen jouw werkgebied ligt. Zorg ervoor dat je jouw
          werkgebieden toevoegt aan je{' '}
          <Link to="/-/settings/offices">vestigingen</Link> in de instellingen.
        </Body>

        <ToggleCheckbox
          label={text.helpWithBiddingLabel}
          value={values?.content?.report.cta.helpWithBidding || false}
          onChange={() => {
            onUpdateCta(
              'helpWithBidding',
              !(values?.content?.report.cta.helpWithBidding || false),
            );
          }}
          containerProps={{
            direction: 'row',
            gap: 'base',
            justification: 'space-between',
            align: 'center',
            width: '100%',
          }}
        />

        <Divider margin={[null]} />

        <ToggleCheckbox
          label={text.helpWithViewingLabel}
          value={values?.content?.report.cta.helpWithViewing || false}
          onChange={() => {
            onUpdateCta(
              'helpWithViewing',
              !(values?.content?.report.cta.helpWithViewing || false),
            );
          }}
          containerProps={{
            direction: 'row',
            gap: 'base',
            justification: 'space-between',
            align: 'center',
            width: '100%',
          }}
        />
      </Container>
    </AppDetailsContainer>
  );
};

const Container = styled(JustificationContainer)`
  gap: ${({ theme }) => theme.space('base')};
`;

export default Report;
